import { CaseData, DocumentsModel, MedicalCoverageType, PartyModel } from "@/types/case";
import { calcDocumentsIA2025 } from "./2025/IA/documentsCalcs";
import { validateInputIA2025 } from "./2025/IA/validations";
import { calcDocumentsIA2026 } from "./2026/IA/documentsCalcs";
import { validateInputIA2026 } from "./2026/IA/validations";
import { taxRules2026 } from "./2026/taxRules";
import { taxRules2025 } from "./2025/taxRules";
import { TaxRules } from "../types";
import { taxRules2023 } from "./2023/taxRules";
import { taxRules2024 } from "./2024/taxRules";
import { taxRules2021 } from "./2021/taxRules";
import { taxRules2022 } from "./2022/taxRules";
import { CalcVersion, CalcVersionKey } from "./calcVersionList";

export { listCalcVersions } from "./calcVersionList";
export type { CalcVersion } from "./calcVersionList";


export function getTaxRulesForYear(year: number, stateCode: string): TaxRules {
    const yearAndState = `${year}-${stateCode}`;
    switch (yearAndState) {
        case '2026-IA':
            return taxRules2026;
        case '2025-IA':
            return taxRules2025;
        case '2024-IA':
            return taxRules2024;
        case '2023-IA':
            return taxRules2023;
        case '2022-IA':
            return taxRules2022;
        case '2021-IA':
            return taxRules2021;

        default:
            throw new Error(`Unsupported state and year: ${yearAndState}`);
    }
}


interface CalcVersionEntry extends CalcVersion {
    validate: (caseData: CaseData) => CaseData;
    calcDocuments: (caseData: CaseData) => void;
}

/**
 * Every state/year combination that has full calculations and documents in this build. Adding a
 * combination here makes it exist; whether users can pick it is the per-environment
 * calc_version_availability row an admin toggles on Configuration > Calculations.
 * See docs/ADDING_A_CALC_VERSION.md.
 */
const CALC_VERSIONS: Record<CalcVersionKey, CalcVersionEntry> = {
    '2026-IA': { stateCode: 'IA', year: 2026, validate: validateInputIA2026, calcDocuments: c => calcDocumentsIA2026(c, true, true) },
    '2025-IA': { stateCode: 'IA', year: 2025, validate: validateInputIA2025, calcDocuments: c => calcDocumentsIA2025(c, true, true) },
};

/** The registry entry for a built combination, or undefined. */
function findCalcVersion(yearAndState: string): CalcVersionEntry | undefined {
    return (CALC_VERSIONS as Record<string, CalcVersionEntry | undefined>)[yearAndState];
}

/**
 * 
 * @param caseData - The case data to validate
 * @returns Same CaseData object with validation and metadata.errors properties updated
 */
export function validateInputVersioned(caseData: CaseData): CaseData {
    const year = caseData.basic.taxYear ?? new Date().getFullYear();
    const yearAndState = `${year}-${caseData.basic.stateCode}`;
    const version = findCalcVersion(yearAndState);
    if (version) {
        return version.validate(caseData);
    }
    if (year < 2025) {
        return caseData;
    }
    throw new Error(`Unsupported state and year: ${yearAndState}`);
}

/**
 * 
 * @param caseData - The case data to calculate documents for
 * @returns Same CaseData object with calculated values in documents property
 */
export function calcDocumentsVersioned(caseData: CaseData): void {
    const year = caseData.basic.taxYear ?? new Date().getFullYear();
    const yearAndState = `${year}-${caseData.basic.stateCode}`;
    const version = findCalcVersion(yearAndState);
    if (version) {
        version.calcDocuments(caseData);
        return;
    }
    if (year < 2025) {
        // do nothing
        return;
    }
    throw new Error(`Unsupported state and year: ${yearAndState}`);
}
